import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { TikTokUserAllowance } from 'containers/TikTokPostModal';
import { TikTokPrivacyLevelOptionName } from 'redux/middleware/api/third-party-authentication-service/types';
import { tikTokCreatorInfoSelector } from 'redux/modules/social';

export interface UseTikTokComposerValuesResult {
  isDisclosureVisible: boolean;
  brandedContentSelected: boolean;
  privacyLevel: TikTokPrivacyLevelOptionName | undefined;
  userAllowance: TikTokUserAllowance[];
  brandContent: boolean;
  brandOrganic: boolean;
  onVisibilityChange: (isDisclosureVisible: boolean) => void;
  onPrivacyLevelChange: (
    privacyLevel: TikTokPrivacyLevelOptionName | undefined,
  ) => void;
  onUserAllowanceChange: (userAllowance: TikTokUserAllowance[]) => void;
  onBrandContentChange: (brandContent: boolean) => void;
  onBrandOrganicChange: (brandOrganic: boolean) => void;
  resetComposerValues: () => void;
}

export default function useTikTokComposerValues(): UseTikTokComposerValuesResult {
  const [isDisclosureVisible, setIsDisclosureVisible] = useState<boolean>();
  const [privacyLevel, setPrivacyLevel] = useState<
    TikTokPrivacyLevelOptionName | undefined
  >();
  const [userAllowance, setUserAllowance] = useState<TikTokUserAllowance[]>([]);
  const [brandContent, setBrandContent] = useState<boolean>();
  const [brandOrganic, setBrandOrganic] = useState<boolean>();

  const creatorInfo = useSelector(tikTokCreatorInfoSelector);

  const handleBrandContentChange = useCallback(
    (isBrandedContent: boolean): void => {
      setBrandContent(isBrandedContent);

      if (!isBrandedContent || privacyLevel !== 'selfOnly') {
        return;
      }

      setPrivacyLevel(
        creatorInfo?.isAccountPublic ? 'publicToEveryone' : 'followerOfCreator',
      );
    },
    [creatorInfo, privacyLevel],
  );

  const handleSetDefaultProps = useCallback((): void => {
    setIsDisclosureVisible(undefined);
    setPrivacyLevel(undefined);
    setUserAllowance(undefined);
    setBrandContent(undefined);
    setBrandOrganic(undefined);
  }, []);

  return {
    isDisclosureVisible,
    brandedContentSelected: brandContent,
    privacyLevel,
    userAllowance,
    brandContent,
    brandOrganic,
    onVisibilityChange: setIsDisclosureVisible,
    onPrivacyLevelChange: setPrivacyLevel,
    onUserAllowanceChange: setUserAllowance,
    onBrandContentChange: handleBrandContentChange,
    onBrandOrganicChange: setBrandOrganic,
    resetComposerValues: handleSetDefaultProps,
  };
}
